import { memo, useCallback, useMemo } from "react";
import { literals } from "@/constants/literals";
import Required from "@/components/required/Required";

type optionsLabels = {
  yesLabel: string;
  noLabel: string;
};
export interface LightRadioGroupProps {
  label: string;
  selected: boolean | undefined;
  onChange: (value?: boolean) => void;
  required?: boolean;
  noBefore?: boolean;
  customOptionsLabels?: optionsLabels;
}

const LightRadioGroup = ({
  label,
  selected,
  onChange,
  required,
  noBefore = false,
  customOptionsLabels,
}: LightRadioGroupProps) => {
  const chageToTrue = useCallback(() => {
    onChange(true);
  }, [onChange]);

  const chageToFalse = useCallback(() => {
    onChange(false);
  }, [onChange]);

  const radioElements = useMemo(
    () => [
      {
        id: `${label}-yes`,
        value: "true",
        label: customOptionsLabels?.yesLabel || literals.yes,
        onChange: chageToTrue,
        checked: selected,
      },
      {
        id: `${label}-no`,
        value: "false",
        label: customOptionsLabels?.noLabel || literals.no,
        onChange: chageToFalse,
        checked: selected === false,
      },
    ],
    [label, selected, chageToTrue, chageToFalse, customOptionsLabels],
  );

  return (
    <div
      className="gap-x-1 text-[14px] leading-[16px]"
      data-testid={`radio-group-${label}`}
    >
      <div className="text-ib-pt-color-neutral-800 pb-1 flex flex-row items-center gap-x-1">
        <span>{label}</span>
        {required && <Required />}
      </div>

      <div className="w-full flex flex-row gap-x-2 md:gap-x-8">
        {!noBefore
          ? radioElements.map((radioElement) => (
              <div
                key={radioElement.id}
                className="h-[42px] flex-1 border border-ib-gray-borders rounded-[2px] p-2 flex items-center"
              >
                <input
                  type="radio"
                  id={radioElement.id}
                  name={radioElement.id}
                  value={radioElement.value}
                  className="accent-ib-pt-color-red-500"
                  onChange={radioElement.onChange}
                  checked={radioElement.checked}
                />
                <label
                  htmlFor={radioElement.id}
                  className="text-[14px] leading-5 ml-2 flex-1"
                >
                  {radioElement.label}
                </label>
              </div>
            ))
          : [...radioElements].reverse().map((radioElement) => (
              <div
                key={radioElement.id}
                className="h-[42px] flex-1 border border-ib-gray-borders rounded-[2px] p-2 flex items-center"
              >
                <input
                  type="radio"
                  id={radioElement.id}
                  name={radioElement.id}
                  value={radioElement.value}
                  className="accent-ib-pt-color-red-500"
                  onChange={radioElement.onChange}
                  checked={radioElement.checked}
                />
                <label
                  htmlFor={radioElement.id}
                  className="text-[14px] leading-5 ml-2 flex-1"
                >
                  {radioElement.label}
                </label>
              </div>
            ))}
      </div>
    </div>
  );
};

export default memo(LightRadioGroup);
